import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import ErrorText from "@/components/universal/error-text";
import { getGetEntityQueryOptions } from "@/requests/gen/react-query/entity";
import { EntityCrumbs } from "@/routes/_private/_admin/entity/-crumbs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  EntityDetailsTab,
  ServicesTab,
  VisibilityTab,
  ShipEngineAccountsTab,
  EntityNotificationsTab,
  DocumentsTab,
  PermissionsTab,
  FulfillerAccountsTab,
  PaymentDashboardTab,
} from "./-tabs/index";
import { EntityTitleCard } from "./-tabs/title-card";
import AuthorizedStaff from "./-authorized-staff";
import { useAuth } from "@/auth/use-auth";
import { PermissionEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import numericId from "@/lib/numeric-id";

export const Route = createFileRoute("/_private/entity/$entityId/")({
  loader: ({ context: { queryClient }, params: { entityId } }) =>
    queryClient.ensureQueryData(getGetEntityQueryOptions(numericId(entityId))),
  component: EntityDetailsPage,
});

function EntityDetailsPage() {
  const { entityId } = Route.useParams();
  const {
    data: entity,
    error,
    refetch,
  } = useSuspenseQuery(
    suspenseOptions(getGetEntityQueryOptions(numericId(entityId))),
  );

  const { authUser } = useAuth();
  const hasAdministrateUsers =
    authUser?.permissions?.includes(PermissionEnum.administrate_users) ?? false;
  const hasEntityFinance =
    authUser?.permissions?.includes(PermissionEnum.entity_finance) ?? false;

  return (
    <div>
      <title>{`${entity?.name} - Legrande Health Portal`}</title>
      <EntityCrumbs />
      <div className="space-y-6">
        {error && <ErrorText text="Failed to load entity details" />}
        {entity && (
          <>
            <EntityTitleCard entity={entity} />
            <Tabs className="w-full" defaultValue={"details"}>
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="services">Services</TabsTrigger>
                <TabsTrigger value="visibility">Visibility</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="fulfiller_accounts">
                  Fulfillment
                </TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                {hasAdministrateUsers && (
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                )}
                {entity.services.some(
                  (service) => service.service === "patient_360",
                ) && (
                  <TabsTrigger value="staff_operations">
                    Staff & Operations
                  </TabsTrigger>
                )}
                {hasEntityFinance && (
                  <TabsTrigger value="payments">Payments</TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="details">
                <EntityDetailsTab entity={entity} refetch={refetch} />
              </TabsContent>
              <TabsContent value="services">
                <ServicesTab entity={entity} />
              </TabsContent>
              <TabsContent value="visibility">
                <VisibilityTab entity={entity} />
              </TabsContent>
              <TabsContent value="notifications">
                <EntityNotificationsTab
                  entity_id={entity.entity_id}
                  entityServices={entity.services}
                  contacts={entity.entity_contacts}
                  refetch={refetch}
                />
              </TabsContent>
              {hasAdministrateUsers && (
                <TabsContent value="permissions">
                  <PermissionsTab entity={entity} refetchEntity={refetch} />
                </TabsContent>
              )}
              <TabsContent value="fulfiller_accounts">
                <div className="space-y-6">
                  <FulfillerAccountsTab entity={entity} />
                  <ShipEngineAccountsTab entity={entity} />
                </div>
              </TabsContent>
              <TabsContent value="documents">
                <DocumentsTab entityId={entity.entity_id} />
              </TabsContent>
              {entity.services.some(
                (service) => service.service === "patient_360",
              ) && (
                <TabsContent value="staff_operations">
                  <AuthorizedStaff entity={entity} />
                </TabsContent>
              )}
              {hasEntityFinance && (
                <TabsContent value="payments">
                  <PaymentDashboardTab entity={entity} refetch={refetch} />
                </TabsContent>
              )}
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
